// Plain-text <-> HTML helpers, shared by the server and the agent UI (moved
// here from server/src/text-format.js on 2026-09-29; that file re-exports
// this one, so every server import keeps working unchanged). One
// implementation on purpose: an eBay reply box must show byte-for-byte what
// the eBay send path transmits, so the box's normalizer and the send-time
// backstop have to be the same code (docs/decisions.md, 2026-09-29).
//
// The server uses it to format a drafted reply for the rich email editor
// (ai/pipeline.js), to flatten a DB template for a plain-text channel
// (ai/return-refund.js), and as the send-time backstop for every eBay send
// (services/send-approved-reply.js, services/action-executor.js). The UI
// uses it for every text loaded into an eBay reply or step box
// (SuggestionPanel.jsx). ui/src/richTextConversion.js keeps its own
// converter for the rich email editor; this module does not replace it.
// plainTextToHtmlParagraphs/looksLikeHtml still mirror that file's
// plainTextToEditableHtml/looksLikeHtml (blank-line paragraphs, single
// newline -> <br>, HTML-escaped).

export function escapeHtmlText(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

export function plainTextToHtmlParagraphs(text) {
  if (!text) return ''
  return text.split(/\n{2,}/).map((p) => `<p>${escapeHtmlText(p).replace(/\n/g, '<br>')}</p>`).join('')
}

// Real links in an email (2026-10-20): every bare http(s) URL in the TEXT of an
// HTML body becomes <a href="URL">URL</a>, the link text the full URL. Text
// only: never inside a tag, an existing <a>...</a>, <style> or <script>.
// mailto: and tel: are never touched (only http and https are matched). The
// input is HTML (a plain-text reply after plainTextToHtmlParagraphs()), so a
// "&" in a URL is already "&amp;", which is also correct inside href. Trailing
// punctuation (". , ; : ! ? ) ] } ' \"") and a trailing escaped ">" stay out
// of the link; a ")" closing a "(" inside the URL stays in. Email only: the
// eBay path is plain text and never calls this, and a Walmart reply is never
// linkified (its links are refused at send).
const LINKIFY_URL_RE = /\bhttps?:\/\/[^\s<>"']+/gi
const LINKIFY_TRAILING_RE = /(?:[.,;:!?)\]}'"]|&gt;|&lt;|&quot;|&#39;)+$/
function linkifyTextSegment(segment) {
  return segment.replace(LINKIFY_URL_RE, (match) => {
    let url = match
    let tail = ''
    const t = LINKIFY_TRAILING_RE.exec(url)
    if (t) {
      tail = t[0]
      url = url.slice(0, -tail.length)
      // A ")" that closes a "(" inside the URL belongs to it.
      while (tail.startsWith(')') && (url.split('(').length - 1) > (url.split(')').length - 1)) {
        url += ')'
        tail = tail.slice(1)
      }
    }
    if (!/^https?:\/\/[^/]/i.test(url)) return match
    return `<a href="${url.replace(/"/g, '&quot;')}">${url}</a>${tail}`
  })
}
export function linkifyHtml(html) {
  if (!html) return html ?? ''
  const parts = String(html).split(/(<[^>]*>)/)
  let inAnchor = 0
  let inRaw = null
  return parts.map((part) => {
    if (part.startsWith('<')) {
      const tag = /^<\s*(\/)?\s*([a-z0-9]+)/i.exec(part)
      if (tag) {
        const closing = Boolean(tag[1])
        const name = tag[2].toLowerCase()
        if (name === 'a') inAnchor = Math.max(0, inAnchor + (closing ? -1 : (/\/\s*>$/.test(part) ? 0 : 1)))
        if ((name === 'style' || name === 'script')) inRaw = closing ? null : name
      }
      return part
    }
    if (inAnchor || inRaw || !part) return part
    return linkifyTextSegment(part)
  }).join('')
}

// A string containing ANY tag is treated as HTML in full — same heuristic
// ui/src/richTextConversion.js's own looksLikeHtml() uses (also used by
// templatePreview.js).
export function looksLikeHtml(value) {
  return /<[a-z][\s\S]*>/i.test(value ?? '')
}

// Same shape as looksLikeHtml() above, but for the ESCAPED form (&lt;p&gt;)
// — a value that never had a real '<' character but still needs unescaping
// before it can be treated as HTML (see decodeHtmlEntities() below).
export function looksLikeEscapedHtml(value) {
  return /&lt;[a-z][\s\S]*&gt;/i.test(value ?? '')
}

const ENTITY_REPLACEMENTS = [
  [/&nbsp;/g, ' '],
  [/&ldquo;|&rdquo;/g, '"'],
  [/&lsquo;|&rsquo;/g, "'"],
  [/&mdash;/g, '—'],
  [/&ndash;/g, '–'],
  [/&hellip;/g, '…'],
  [/&quot;/g, '"'],
  [/&#39;|&apos;/g, "'"],
]

function decodeHtmlEntitiesOnce(s) {
  for (const [pattern, replacement] of ENTITY_REPLACEMENTS) s = s.replace(pattern, replacement)
  s = s.replace(/&#(\d+);/g, (_, n) => { try { return String.fromCodePoint(Number(n)) } catch { return '' } })
  s = s.replace(/&#x([0-9a-fA-F]+);/g, (_, h) => { try { return String.fromCodePoint(parseInt(h, 16)) } catch { return '' } })
  // &amp; last, so a literal "&amp;lt;" (an ampersand-escaped "&lt;") only
  // ever unwraps its OWN outer escaping in this one pass — the bounded loop
  // in decodeHtmlEntities() below is what fully unwinds a genuinely
  // double-encoded value across passes, not this single-pass ordering.
  return s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
}

// Bounded so a double (or triple) HTML-escaped value — e.g. a template's
// "&lt;p&gt;" tags that got escaped a second time somewhere along the way —
// fully unwinds to real characters/tags, without looping forever on
// adversarial input. Stable, ordinary text (including a single legitimate
// "&amp;") converges in one pass and the loop exits early.
export function decodeHtmlEntities(value) {
  let s = String(value ?? '')
  for (let i = 0; i < 3; i++) {
    const next = decodeHtmlEntitiesOnce(s)
    if (next === s) break
    s = next
  }
  return s
}

// Internal token marking a real single line break (a <br>, or one bullet's
// own start) so the blank-line collapsing below can't touch it — a literal
// '\n' at that point only ever came from an actual source '\n' character,
// never from a tag. Exported for callers that read htmlToPlainTextTags()'s
// output before finishPlainText() resolves it.
export const LINE_BREAK_TOKEN = '\u0000BR\u0000'

// HTML -> plain text, stopping just short of resolving LINE_BREAK_TOKEN back
// into a real '\n' — the shared first half of htmlToPlainText() below and of
// ai/return-refund.js's own htmlTemplateToPlainText(), which needs to insert
// its own extra rule in between the two steps (see that function).
export function htmlToPlainTextTags(html) {
  if (!html) return ''
  let s = decodeHtmlEntities(html)
  // <br> -> a single real line break, via the protected token above. Any
  // <br>, with attributes too (2026-10-28): the Templates editor's tight
  // break is <br data-tight="true">, which the catch-all tag strip below
  // used to remove, joining its two lines.
  s = s.replace(/<\s*br\b[^>]*>/gi, LINE_BREAK_TOKEN)
  // <li> -> its own "• " line — tight, not blank-line separated from its
  // neighbors; a wrapping <ul>/<ol> (below, among the closing block tags)
  // still gets a blank line around the list as a whole.
  s = s.replace(/<\s*li[^>]*>/gi, `${LINE_BREAK_TOKEN}• `)
  s = s.replace(/<\s*\/\s*li\s*>/gi, '')
  // A link keeps its URL (eBay is plain text, so the buyer needs the address
  // itself): the bare URL when the link text is empty or is the URL, and
  // "Text: URL" otherwise. A mailto: link shows the address alone. Runs
  // before the catch-all tag strip below, which would leave only the text.
  s = s.replace(/<\s*a\b[^>]*?\bhref\s*=\s*(?:"([^"]*)"|'([^']*)')[^>]*>([\s\S]*?)<\s*\/\s*a\s*>/gi, (match, dq, sq, inner) => {
    const href = (dq ?? sq ?? '').trim()
    const text = inner.replace(/<\/?[a-z][^>]*>/gi, '').trim()
    if (!href) return text
    const address = href.toLowerCase().startsWith('mailto:') ? href.slice('mailto:'.length) : href
    if (!text || text === href || text === address) return address
    return `${text}: ${address}`
  })
  // Every other block-level closing tag -> a blank-line paragraph break.
  s = s.replace(/<\s*\/\s*(p|div|h[1-6]|ul|ol|tr|table|blockquote)\s*>/gi, '\n\n')
  // Strip every remaining tag — opening block tags (no spacing meaning of
  // their own once their closer/next sibling has already produced one) and
  // any inline tag (<b>, <a href="...">, etc.). Requires a letter right
  // after '<' or '</' (same shape looksLikeHtml() above already uses) so
  // ordinary prose using '<'/'>' as comparison operators (e.g. "if x < y and
  // y > z") is never mistaken for a tag and stripped.
  return s.replace(/<\/?[a-z][^>]*>/gi, '')
}

// Finishes what htmlToPlainTextTags() started: collapses 3+ blank lines to
// exactly one, resolves LINE_BREAK_TOKEN back into a real single '\n', trims
// stray spaces off each line, and trims the whole result.
export function finishPlainText(s) {
  let out = s.replace(/\n{3,}/g, '\n\n')
  out = out.split(LINE_BREAK_TOKEN).join('\n')
  out = out.split('\n').map((line) => line.replace(/[ \t]{2,}/g, ' ').trim()).join('\n')
  return out.replace(/\n{3,}/g, '\n\n').trim()
}

// The one shared HTML -> plain-text normalizer for every plain-text send
// channel (eBay member messages, eBay INR/case messages, and the send-time
// backstop in send-approved-reply.js/action-executor.js) — never add a
// second converter (see ai/return-refund.js's htmlTemplateToPlainText(),
// which wraps THIS function with its own DB-template-specific rules on top,
// rather than reimplementing tag conversion). </p> and <p> boundaries become
// a blank line, <br> a single newline, <li> a "• " line, tags are stripped
// and entities (including a doubly-escaped tag) are decoded.
//
// Deliberately does NOT touch a bare single '\n' with no HTML around it —
// plain text elsewhere in this codebase already uses a single newline as a
// real, intentional line break (e.g. an address block built line-by-line),
// and doubling it here would silently invent blank lines that were never
// there. A DB template goes through templateHtmlToPlainText() below (the
// Templates editor's own layout, 2026-10-28).
export function htmlToPlainText(html) {
  return finishPlainText(htmlToPlainTextTags(html))
}

// True when the text carries real tags or escaped tags (&lt;p&gt;), the only
// text the eBay paths below ever convert.
export function containsHtml(value) {
  return looksLikeHtml(value) || looksLikeEscapedHtml(value)
}

// What an eBay reply or step box holds, and what the eBay send path
// transmits. Plain text passes through byte-for-byte, keeping every blank
// line, indent and trailing space the agent typed. Only text that actually
// carries HTML goes through htmlToPlainText(). The UI runs it once, when
// text is loaded into a box, never on a keystroke. The send backstops run it
// again, and a change there means the box and the send disagreed.
export function ebayPlainText(value) {
  if (value == null) return value
  return containsHtml(value) ? htmlToPlainText(value) : value
}

// A DB template (the `templates` table, or one picked in the UI) flattened
// for a plain-text channel: exactly the layout the Templates editor shows
// for it (2026-10-28, Jenn: "templates are saved with line breaks exactly
// the way we want them"; ui/src/TemplateRichEditor.jsx, loaded through
// ui/src/richTextConversion.js's toEditableHtml()). Line for line:
//   - a plain-text body (no tag): the editor makes a paragraph of each run
//     between blank lines (\n\n) and a line break (<br>) of each single \n.
//     So a blank line stays one blank line and a single \n stays a single
//     line break; nothing is added, joined or split;
//   - an HTML body: a paragraph (<p>, and the other block tags) is a blank
//     line between; a <br> (bare or the editor's data-tight one, both one
//     line break in the editor) is a single line break; a list item is its
//     own line; a literal newline in the HTML source is only whitespace, as
//     in the editor.
// Until 2026-10-28 every bare single '\n' became a blank line here, which the
// editor never showed. The core of ai/return-refund.js's
// htmlTemplateToPlainText(), which adds merge-field and Reference ID rules.
export function templateHtmlToPlainText(html) {
  const src = String(html ?? '')
  const isHtml = looksLikeHtml(src) || looksLikeEscapedHtml(src)
  return finishPlainText(htmlToPlainTextTags(isHtml ? src.replace(/[ \t]*\r?\n[ \t]*/g, ' ') : src.replace(/\r\n/g, '\n')))
}

// The resolved form of the draft footer "[AGENT_FIRST_NAME] | <signature>":
// "Sam | 1A Auto" with a first name, the bare signature without one, and
// always the bare signature for a company whose signature never carries a
// name (signatureIncludesAgentName: false, i.e. AM). The server's
// companies.js resolveSignature() and the UI both call this.
export function resolvedSignatureFor({ signature, signatureIncludesAgentName }, firstName) {
  if (signatureIncludesAgentName === false) return signature
  return firstName ? `${firstName} | ${signature}` : signature
}

// Resolves every agent-name placeholder the way the send path does: the
// footer pair first (so a missing name leaves no dangling " | "), then any
// other [AGENT_FIRST_NAME] token becomes the first name, or nothing. The UI
// applies this to text it puts in a box, so the box already holds exactly
// what services/send-approved-reply.js would transmit.
export function resolveAgentNamePlaceholders(text, signatureInfo, firstName) {
  if (!text) return text
  const footer = `[AGENT_FIRST_NAME] | ${signatureInfo.signature}`
  return text
    .replaceAll(footer, resolvedSignatureFor(signatureInfo, firstName))
    .replaceAll('[AGENT_FIRST_NAME]', firstName ?? '')
}

// The exact text an eBay reply or step box is loaded with: normalized first
// (only if it carries HTML), then the agent-name placeholders resolved. The
// UI calls this once per load; the server's parity tests call the same
// function, so "what the box shows" can never be a copy of the rule.
export function ebayBoxText(text, signatureInfo, firstName) {
  return resolveAgentNamePlaceholders(ebayPlainText(text), signatureInfo, firstName)
}
